import { useEffect, useState } from "react";
import { ArrowSquareOut, Check, Money, Receipt, SpinnerGap, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, fileUrl, formatApiErrorDetail } from "../../lib/api";
import { formatMonthLabel, formatRupiahValue } from "../PaymentDialog";

const STATUS_LABELS = { settlement: "LUNAS (settlement)", capture: "LUNAS (capture)", pending: "Menunggu pembayaran", expire: "Kedaluwarsa", cancel: "Dibatalkan", deny: "Ditolak", failure: "Gagal" };
const CHANNEL_LABELS = { bank_transfer: "Transfer Bank / Virtual Account", qris: "QRIS", gopay: "GoPay", shopeepay: "ShopeePay", credit_card: "Kartu Kredit", echannel: "Mandiri Bill", cstore: "Gerai Retail" };

/** Panel bukti untuk pembayaran online: pembayaran Midtrans tidak punya foto bukti,
    buktinya adalah rekaman transaksi di server Midtrans — ditarik langsung saat admin klik. */
function MidtransProofPanel({ submission, index, onChanged }) {
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(false);

    const check = async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/finance/payments/${submission.id}/midtrans-detail`);
            setDetail(data);
            if (data.auto_approved) {
                toast.success("Ternyata sudah lunas di Midtrans — otomatis disetujui & kwitansi diterbitkan");
                onChanged();
            }
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="shrink-0 w-full lg:w-64 border border-green-600/30 bg-green-50/50 p-4" data-testid={`midtrans-proof-${index}`}>
            <p className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-green-700"><Receipt size={15} /> Bukti Pembayaran Online</p>
            {!detail ? (
                <button
                    type="button"
                    onClick={check}
                    disabled={loading}
                    data-testid={`midtrans-check-${index}`}
                    className="mt-3 w-full inline-flex items-center justify-center gap-2 border border-green-600/50 text-green-700 hover:bg-green-600 hover:text-white disabled:opacity-60 text-[10px] uppercase tracking-[0.15em] px-4 py-3 transition-colors"
                >
                    {loading ? <SpinnerGap size={14} className="animate-spin" /> : <ArrowSquareOut size={14} />}
                    {loading ? "Memeriksa..." : "Cek Status ke Midtrans"}
                </button>
            ) : !detail.found ? (
                <p className="mt-3 text-xs text-neutral-900/70" data-testid={`midtrans-notfound-${index}`}>{detail.note}</p>
            ) : (
                <dl className="mt-3 space-y-1.5 text-xs" data-testid={`midtrans-detail-${index}`}>
                    <div className="flex justify-between gap-2"><dt className="text-racing-muted">Status</dt><dd className="font-semibold text-neutral-900 text-right">{STATUS_LABELS[detail.transaction_status] || detail.transaction_status}</dd></div>
                    <div className="flex justify-between gap-2"><dt className="text-racing-muted">Channel</dt><dd className="text-neutral-900 text-right">{CHANNEL_LABELS[detail.payment_type] || detail.payment_type || "-"}</dd></div>
                    {detail.bank && <div className="flex justify-between gap-2"><dt className="text-racing-muted">Bank</dt><dd className="text-neutral-900 uppercase text-right">{detail.bank}</dd></div>}
                    {detail.va_number && <div className="flex justify-between gap-2"><dt className="text-racing-muted">No. VA</dt><dd className="text-neutral-900 text-right break-all">{detail.va_number}</dd></div>}
                    <div className="flex justify-between gap-2"><dt className="text-racing-muted">Dibayar</dt><dd className="text-neutral-900 text-right">Rp {Number(detail.gross_amount || 0).toLocaleString("id-ID")}</dd></div>
                    {detail.settlement_time && <div className="flex justify-between gap-2"><dt className="text-racing-muted">Waktu Lunas</dt><dd className="text-neutral-900 text-right">{detail.settlement_time}</dd></div>}
                    <div className="flex justify-between gap-2"><dt className="text-racing-muted">Dibuat</dt><dd className="text-neutral-900 text-right">{detail.transaction_time || "-"}</dd></div>
                </dl>
            )}
            <a
                href={`https://dashboard.midtrans.com/transactions/${submission.id}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`midtrans-dashboard-link-${index}`}
                className="mt-3 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] text-green-700 hover:text-green-900 underline underline-offset-2"
            >
                Buka di Dashboard Midtrans <ArrowSquareOut size={12} />
            </a>
        </div>
    );
}

const STATUS_TABS = [
    { key: "pending", label: "Menunggu Konfirmasi" },
    { key: "approved", label: "Disetujui" },
    { key: "rejected", label: "Ditolak" },
];

export default function PaymentSubmissionsTab() {
    const [status, setStatus] = useState("pending");
    const [items, setItems] = useState(null);
    const [busy, setBusy] = useState("");
    const [rejectReason, setRejectReason] = useState({});
    const [rejecting, setRejecting] = useState("");

    const load = () => {
        api.get("/admin/payment-submissions", { params: { status } })
            .then((r) => setItems(r.data))
            .catch(() => toast.error("Gagal memuat pengajuan pembayaran"));
    };

    useEffect(() => {
        setItems(null);
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [status]);

    const approve = async (submission) => {
        setBusy(submission.id);
        try {
            const r = await api.post(`/admin/payment-submissions/${submission.id}/approve`);
            toast.success(r.data.message);
            load();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
            load();
        } finally {
            setBusy("");
        }
    };

    const reject = async (submission) => {
        setBusy(submission.id);
        try {
            const r = await api.post(`/admin/payment-submissions/${submission.id}/reject`, { reason: rejectReason[submission.id] || "" });
            toast.success(r.data.message);
            setRejecting("");
            load();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
            load();
        } finally {
            setBusy("");
        }
    };

    return (
        <div data-testid="payment-submissions-tab">
            <h2 className="font-display uppercase text-2xl text-neutral-900 mb-2">Konfirmasi Pembayaran Orang Tua</h2>
            <p className="text-sm text-racing-muted mb-6">Periksa bukti pembayaran dari orang tua — foto bukti untuk transfer manual, atau cek status langsung ke Midtrans untuk pembayaran online. Menyetujui akan otomatis mencatat lunas, menerbitkan kwitansi, dan mengirim email kwitansi ke orang tua.</p>

            <div className="flex flex-wrap gap-2 mb-6" data-testid="payment-submissions-filters">
                {STATUS_TABS.map((tab) => (
                    <button
                        key={tab.key}
                        type="button"
                        onClick={() => setStatus(tab.key)}
                        data-testid={`payment-submissions-filter-${tab.key}`}
                        className={`px-5 py-2.5 text-xs uppercase tracking-[0.2em] border transition-colors ${
                            status === tab.key ? "bg-racing-red border-racing-red text-white" : "bg-white border-racing-line text-racing-muted hover:border-racing-red hover:text-racing-red"
                        }`}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            {items === null ? (
                <p className="text-racing-muted" data-testid="payment-submissions-loading">Memuat pengajuan...</p>
            ) : items.length === 0 ? (
                <div className="border border-racing-line bg-white p-10 text-center" data-testid="payment-submissions-empty">
                    <p className="text-racing-muted">Tidak ada pengajuan dengan status ini.</p>
                </div>
            ) : (
                <div className="space-y-5" data-testid="payment-submissions-list">
                    {items.map((submission, index) => (
                        <div key={submission.id} className="border border-racing-line bg-white p-5" data-testid={`payment-submission-card-${index}`}>
                            <div className="flex flex-col lg:flex-row gap-5">
                                {submission.proof_url ? (
                                    <a href={fileUrl(submission.proof_url)} target="_blank" rel="noopener noreferrer" className="shrink-0" data-testid={`payment-proof-${index}`}>
                                        <img src={fileUrl(submission.proof_url)} alt="Bukti pembayaran" className="w-full lg:w-44 max-h-44 object-contain border border-racing-line bg-racing-surface" />
                                    </a>
                                ) : submission.method === "midtrans" ? (
                                    <MidtransProofPanel submission={submission} index={index} onChanged={load} />
                                ) : (
                                    <div className="shrink-0 w-full lg:w-44 border border-dashed border-racing-line bg-racing-surface p-4 text-center" data-testid={`payment-no-proof-${index}`}>
                                        <p className="text-[10px] uppercase tracking-[0.15em] text-racing-muted">Tidak ada bukti terunggah</p>
                                    </div>
                                )}
                                <div className="flex-1 min-w-0">
                                    <div className="flex flex-wrap items-center gap-3">
                                        <p className="font-display uppercase text-xl text-neutral-900" data-testid={`payment-athlete-${index}`}>{submission.athlete_name}</p>
                                        <span className={`text-[10px] uppercase tracking-[0.15em] px-2 py-1 border ${submission.type === "registration" ? "border-racing-gold text-racing-golddeep" : "border-racing-line text-racing-muted"}`}>
                                            {submission.type === "registration" ? "Pendaftaran + Iuran Pertama" : "Iuran Bulanan"}
                                        </span>
                                        <span className={`text-[10px] uppercase tracking-[0.15em] px-2 py-1 border ${submission.method === "midtrans" ? "border-green-600/50 text-green-700" : "border-racing-line text-racing-muted"}`} data-testid={`payment-method-${index}`}>
                                            {submission.method === "midtrans" ? "Midtrans Online" : "QRIS ShopeePay"}
                                        </span>
                                    </div>
                                    <p className="mt-1 text-sm text-racing-muted">
                                        {submission.type === "registration"
                                            ? `Biaya pendaftaran + iuran ${formatMonthLabel(submission.months?.[0])}`
                                            : (submission.months || []).map(formatMonthLabel).join(", ")}
                                        {" • "}{new Date(submission.created_at).toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric" })}
                                    </p>
                                    <p className="mt-2 font-display text-3xl text-racing-red" data-testid={`payment-amount-${index}`}>{formatRupiahValue(submission.amount)}</p>
                                    {submission.note && <p className="mt-1 text-xs text-racing-muted">Catatan: {submission.note}</p>}

                                    {submission.status === "pending" && (
                                        <div className="mt-4 flex flex-col gap-3">
                                            <div className="flex flex-wrap gap-2">
                                                <button
                                                    type="button"
                                                    onClick={() => approve(submission)}
                                                    disabled={busy === submission.id}
                                                    data-testid={`payment-approve-${index}`}
                                                    className="inline-flex items-center gap-2 bg-racing-red hover:bg-racing-redhover disabled:opacity-60 text-white text-[10px] uppercase tracking-[0.2em] px-5 py-3 transition-colors"
                                                >
                                                    {busy === submission.id ? <SpinnerGap size={14} className="animate-spin" /> : <Check size={14} weight="bold" />} Setujui & Terbitkan Kwitansi
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => setRejecting(rejecting === submission.id ? "" : submission.id)}
                                                    data-testid={`payment-reject-${index}`}
                                                    className="inline-flex items-center gap-2 border border-racing-line text-racing-muted hover:text-racing-red hover:border-racing-red text-[10px] uppercase tracking-[0.2em] px-5 py-3 transition-colors"
                                                >
                                                    <X size={14} weight="bold" /> Tolak
                                                </button>
                                            </div>
                                            {rejecting === submission.id && (
                                                <div className="flex flex-col sm:flex-row gap-2" data-testid={`payment-reject-form-${index}`}>
                                                    <input
                                                        type="text"
                                                        value={rejectReason[submission.id] || ""}
                                                        onChange={(e) => setRejectReason({ ...rejectReason, [submission.id]: e.target.value })}
                                                        placeholder="Alasan penolakan (dikirim ke WhatsApp orang tua)"
                                                        data-testid={`payment-reject-reason-${index}`}
                                                        className="flex-1 bg-racing-surface border border-racing-line px-4 py-2.5 text-sm focus:outline-none focus:border-racing-red"
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() => reject(submission)}
                                                        disabled={busy === submission.id}
                                                        data-testid={`payment-reject-confirm-${index}`}
                                                        className="bg-neutral-900 hover:bg-black text-white text-[10px] uppercase tracking-[0.2em] px-5 py-2.5 transition-colors"
                                                    >
                                                        Konfirmasi Tolak
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    {submission.status === "approved" && (
                                        <p className="mt-3 text-xs text-green-700" data-testid={`payment-approved-note-${index}`}>Disetujui — kwitansi terbit & WhatsApp terkirim ({submission.payment_ids?.length || 0} catatan pembayaran)</p>
                                    )}
                                    {submission.status === "rejected" && (
                                        <p className="mt-3 text-xs text-racing-red" data-testid={`payment-rejected-note-${index}`}>Ditolak{submission.reject_reason ? `: ${submission.reject_reason}` : ""}</p>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
